import { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Check, AlertCircle } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const BookingForm = ({ defaultRoomId = "", rooms = [] }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    check_in: todayISO(),
    check_out: plusDaysISO(1),
    guests: 2,
    room_id: defaultRoomId,
    special_requests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [avail, setAvail] = useState(null); // {available, checking, message}
  const [formError, setFormError] = useState("");

  useEffect(() => {
    setForm((f) => ({ ...f, room_id: defaultRoomId || f.room_id }));
  }, [defaultRoomId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCheckAvailability = async () => {
    if (!form.check_in || !form.check_out || form.check_out <= form.check_in) {
      toast.error(t("booking.err_dates"));
      return;
    }
    setAvail({ checking: true });
    try {
      const { data } = await api.post("/availability", {
        check_in: form.check_in,
        check_out: form.check_out,
        room_id: form.room_id || null,
      });
      setAvail({
        checking: false,
        available: data.available,
        message: data.available ? t("booking.available") : t("booking.unavailable"),
      });
    } catch (e) {
      setAvail({ checking: false, available: false, message: t("common.error") });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.full_name || !form.email || !form.phone) {
      setFormError(t("booking.err_required"));
      toast.error(t("booking.err_required"));
      return;
    }
    if (form.check_out <= form.check_in) {
      setFormError(t("booking.err_dates"));
      toast.error(t("booking.err_dates"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        room_id: form.room_id || null,
        language: lang,
      });
      setSuccess(true);
      toast.success(t("booking.success_title"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="booking-success"
        className="bg-[#F2EDDF] border border-[#CFC4A8] p-14 text-center"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1E3B2E]/10 text-[#1E3B2E] mb-6">
          <Check size={28} />
        </div>
        <h3 className="font-display text-4xl text-[#0F0E0C]">{t("booking.success_title")}</h3>
        <p className="body-prose mt-4 max-w-md mx-auto">{t("booking.success_body")}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="booking-form"
      className="bg-[#F2EDDF] border border-[#CFC4A8] p-7 sm:p-14 space-y-6"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-[#5C4D3C]">
            {t("booking.full_name")} *
          </Label>
          <Input
            id="full_name"
            data-testid="booking-full-name"
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            required
            className="bg-white border-[#E2D9C8]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#5C4D3C]">
            {t("booking.email")} *
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="booking-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
            className="bg-white border-[#E2D9C8]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#5C4D3C]">
            {t("booking.phone")} *
          </Label>
          <Input
            id="phone"
            data-testid="booking-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            required
            placeholder="+90 ..."
            className="bg-white border-[#E2D9C8] ltr-numbers"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="guests" className="text-[#5C4D3C]">
            {t("booking.guests")} *
          </Label>
          <Select
            value={String(form.guests)}
            onValueChange={(v) => update("guests", parseInt(v, 10))}
          >
            <SelectTrigger
              data-testid="booking-guests"
              className="bg-white border-[#E2D9C8]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`booking-guests-${n}`}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="check_in" className="text-[#5C4D3C]">
            {t("booking.check_in")} *
          </Label>
          <Input
            id="check_in"
            type="date"
            data-testid="booking-check-in"
            min={todayISO()}
            value={form.check_in}
            onChange={(e) => update("check_in", e.target.value)}
            required
            className="bg-white border-[#E2D9C8]"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="check_out" className="text-[#5C4D3C]">
            {t("booking.check_out")} *
          </Label>
          <Input
            id="check_out"
            type="date"
            data-testid="booking-check-out"
            min={form.check_in || todayISO()}
            value={form.check_out}
            onChange={(e) => update("check_out", e.target.value)}
            required
            className="bg-white border-[#E2D9C8]"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="room_id" className="text-[#5C4D3C]">
            {t("booking.room")}
          </Label>
          <Select
            value={form.room_id || "any"}
            onValueChange={(v) => update("room_id", v === "any" ? "" : v)}
          >
            <SelectTrigger
              data-testid="booking-room"
              className="bg-white border-[#E2D9C8]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any" data-testid="booking-room-any">
                {t("booking.room_any")}
              </SelectItem>
              {rooms.map((r) => (
                <SelectItem
                  key={r.id}
                  value={r.id}
                  data-testid={`booking-room-${r.slug}`}
                >
                  {r.name[lang] || r.name.tr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="special_requests" className="text-[#5C4D3C]">
            {t("booking.requests")}
          </Label>
          <Textarea
            id="special_requests"
            data-testid="booking-requests"
            value={form.special_requests}
            onChange={(e) => update("special_requests", e.target.value)}
            placeholder={t("booking.requests_ph")}
            rows={3}
            className="bg-white border-[#E2D9C8]"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2">
        <button
          type="button"
          data-testid="booking-check-availability"
          onClick={handleCheckAvailability}
          className="inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] border border-[#1E3B2E] text-[#1E3B2E] px-6 py-3.5 hover:bg-[#1E3B2E]/5 transition-colors"
        >
          <CalendarIcon size={14} />
          {avail?.checking ? t("booking.checking") : t("booking.check_availability")}
        </button>
        <Button
          type="submit"
          data-testid="booking-submit"
          disabled={submitting}
          className="bg-[#1E3B2E] hover:bg-[#142A20] text-[#FAF6EC] font-mono uppercase tracking-[0.32em] text-[10px] px-10 py-6 rounded-none"
        >
          {submitting ? t("booking.submitting") : t("booking.submit")}
        </Button>
      </div>

      {avail && !avail.checking && (
        <div
          data-testid="availability-result"
          className={`flex items-center gap-2 text-sm ${
            avail.available ? "text-[#4A5D23]" : "text-[#B5441E]"
          }`}
        >
          {avail.available ? <Check size={16} /> : <AlertCircle size={16} />}
          <span>{avail.message}</span>
        </div>
      )}
      {formError && (
        <div
          data-testid="booking-form-error"
          className="flex items-center gap-2 text-sm text-[#B5441E] bg-[#B5441E]/10 border border-[#B5441E]/30 px-4 py-3 rounded-sm"
        >
          <AlertCircle size={16} />
          <span>{formError}</span>
        </div>
      )}
    </form>
  );
};

export default BookingForm;
